import React from 'react';
import {
    BookOpen,
    Wine,
    HelpCircle,
    Star,
    MapPin,
    Check,
    Lightbulb
} from 'lucide-react';

const WineStylesIntro = ({ onStart }) => {
    const categories = [
        {
            title: 'Sparkling',
            color: 'bg-[#FDE68A]', // Yellow-200ish
            textColor: 'text-yellow-900',
            items: ['Fruit Driven Sparkling', 'Complex Sparkling']
        },
        {
            title: 'White',
            color: 'bg-[#FCD34D]', // Yellow-300ish
            textColor: 'text-yellow-900',
            items: ['Fresh, Unoaked White', 'Earthy White', 'Aromatic White', 'Rich, Oaky White']
        },
        {
            title: 'Red',
            color: 'bg-[#EF4444]', // Red-500
            textColor: 'text-white',
            items: ['Mild, Mannered Red', 'Soft, Fruity Red', 'Fresh, Spicy Red', 'Powerful Red']
        },
        {
            title: 'Rosé',
            color: 'bg-[#F9A8D4]', // Pink-300
            textColor: 'text-pink-900',
            items: ['Blush Rosé', 'Dry Rosé']
        },
        {
            title: 'Orange',
            color: 'bg-[#FDBA74]', // Orange-300
            textColor: 'text-orange-900',
            items: ['Aromatic Orange', 'Fruity Orange', 'Rich Orange']
        },
        {
            title: 'Dessert',
            color: 'bg-[#A1887F]', // Brownish
            textColor: 'text-white',
            items: ['Sparkling Dessert', 'Subtle, Aromatic Dessert', 'Rich, Fruity Dessert']
        }
    ];

    return (
        <div className="min-h-screen bg-[#fcf7ec] py-12 px-4 sm:px-6 lg:px-8 font-sans text-gray-900 flex flex-col items-center">
            <div className="max-w-4xl w-full bg-white rounded-3xl shadow-sm p-8 sm:p-10 relative">

                {/* Header Pill */}
                <div className="flex justify-center mb-6">
                    <div className="inline-flex items-center gap-2 bg-orange-50 px-4 py-1.5 rounded-full text-sm font-medium text-orange-700">
                        <BookOpen size={16} className="text-orange-700" />
                        Chapter intro
                    </div>
                </div>

                {/* Title & Description */}
                <div className="text-center mb-10">
                    <h1 className="text-3xl font-bold text-gray-900 mb-4">
                        Understanding Wine Styles
                    </h1>
                    <p className="text-gray-600 text-lg leading-relaxed max-w-2xl mx-auto">
                        Before you begin, review how we classify wine styles so you can
                        recognize and describe wines accurately to guests.
                    </p>
                </div>

                {/* Purple Section - What is a Wine Style? */}
                <div className="bg-[#F5F3FF] rounded-2xl p-6 mb-6">
                    <div className="flex flex-col md:flex-row items-start gap-4">
                        <div className="bg-[#E0D7FE] p-2 rounded-lg mt-1">
                            <Wine className="text-[#7C3AED] w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 text-lg mb-2 text-left">
                                What is a Wine Style?
                            </h3>
                            <p className="text-gray-700 text-sm leading-relaxed text-left mb-2">
                                A wine's style describes how it tastes and feels—its body, texture, and flavor intensity.
                            </p>
                            <p className="text-gray-600 text-sm leading-relaxed text-left">
                                Wine styles help group wines by their sensory characteristics rather than grape or
                                region. When you know a wine's style, you can recommend it confidently, pair it with
                                food, and understand what a guest might expect in the glass.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Green Section - How Styles Are Organized */}
                <div className="bg-[#ECFDF5] rounded-2xl p-6 mb-6">
                    <div className="flex flex-col md:flex-row items-start gap-4">
                        <div className="bg-[#D1FAE5] p-2 rounded-lg mt-1">
                            <HelpCircle className="text-[#059669] w-5 h-5" />
                        </div>
                        <div className="w-full">
                            <h3 className="font-bold text-gray-900 text-lg mb-2 text-left">
                                How Styles Are Organized
                            </h3>
                            <p className="text-gray-700 text-sm mb-3 text-left">
                                Each wine belongs to a category (like Red or White) and then a specific style.
                            </p>
                            <div className="text-sm text-gray-600 space-y-1 text-left">
                                <p><span className="font-bold text-gray-800">For example:</span></p>
                                <ul className="list-disc list-inside space-y-1 ml-1">
                                    <li><span className="font-bold text-gray-800">White wines</span> can be Fresh, Unoaked or Rich, Oaky.</li>
                                    <li><span className="font-bold text-gray-800">Red wines</span> range from Soft, Fruity to Powerful.</li>
                                    <li><span className="font-bold text-gray-800">Sparkling wines</span> can be Fruit Driven or Complex.</li>
                                    <li><span className="font-bold text-gray-800">Rosé wines</span> may be Blush or Dry.</li>
                                    <li><span className="font-bold text-gray-800">Orange wines</span> include Aromatic, Fruity, or Rich styles.</li>
                                    <li><span className="font-bold text-gray-800">Dessert wines</span> vary from Sparkling to Rich, Fruity.</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Yellow Section - Why Styles Matter */}
                <div className="bg-[#FFFBEB] rounded-2xl p-6 mb-10">
                    <div className="flex flex-col md:flex-row items-start gap-4">
                        <div className="bg-[#FEF3C7] p-2 rounded-lg mt-1">
                            <Star className="text-[#D97706] w-5 h-5 fill-current" />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 text-lg mb-2 text-left">
                                Why Styles Matter
                            </h3>
                            <p className="text-gray-700 text-sm font-medium mb-1 text-left">
                                Knowing styles helps you describe, sell, and pair wine.
                            </p>
                            <p className="text-gray-600 text-sm leading-relaxed text-left">
                                Guests rarely remember grape names—but they understand light, crisp, bold, or sweet.
                                By learning styles, you'll connect more naturally with your guests' tastes and increase
                                sales.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Wine Categories Grid */}
                <div className="mb-10">
                    <h3 className="text-center font-bold text-gray-900 text-xl mb-6">
                        Wine Categories & Style Examples
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        {categories.map((cat) => (
                            <div key={cat.title} className={`${cat.color} rounded-xl p-4 flex flex-col items-center h-full`}>
                                <h4 className={`font-bold ${cat.textColor} mb-3`}>{cat.title}</h4>
                                <div className="space-y-2 w-full">
                                    {cat.items.map((item) => (
                                        <div key={item} className="bg-white/40 backdrop-blur-sm rounded-md py-1.5 px-3 text-xs font-medium text-center text-gray-800">
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Blue Section - Key Takeaways */}
                <div className="bg-[#EFF6FF] rounded-2xl p-6 mb-12">
                    <div className="flex flex-col md:flex-row items-start gap-4">
                        <div className="bg-[#DBEAFE] p-1.5 rounded-full mt-0.5">
                            <Lightbulb className="text-[#2563EB] w-4 h-4 fill-current" />
                        </div>
                        <div className="w-full">
                            <h3 className="font-bold text-gray-900 text-base mb-3 text-left">
                                Key Takeaways
                            </h3>
                            <ul className="space-y-2 text-sm text-left">
                                <li className="flex items-start gap-2">
                                    <Check className="text-[#2563EB] w-5 h-5 mt-0.5 flex-shrink-0" />
                                    <span className="text-gray-600">Wine style = how the wine feels and tastes (body, texture, flavor intensity).</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <Check className="text-[#2563EB] w-5 h-5 mt-0.5 flex-shrink-0" />
                                    <span className="text-gray-600">You'll tag and recognize each wine's style in this lesson.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <Check className="text-[#2563EB] w-5 h-5 mt-0.5 flex-shrink-0" />
                                    <span className="text-gray-600">This knowledge helps in upselling and confident menu storytelling.</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Start Button */}
                <div className="flex justify-center">
                    <button
                        onClick={onStart}
                        className="bg-[#A01B3F] hover:bg-[#8a1736] text-white font-bold py-3 px-10 rounded-xl transition-all shadow-md"
                    >
                        Start
                    </button>
                </div>

            </div>
        </div>
    );
};

export default WineStylesIntro;
